import React, { useState } from "react";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { Icons } from "./ui/icons";
import {
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from "./ui/table";
import { cn, getBadgeVariant, getBackendErrorMessage } from "../lib/utils";
import {
  ArrowUpRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import EditableTD from "./editable-td";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { useIsMobile } from "../hooks/use-is-mobile";
import MobileDataList from "./mobile/mobile-data-list";

/** Resolves nested accessors like "warehouse.name". */
const getValue = (obj: any, path: string) =>
  path.split(".").reduce((current, key) => current?.[key], obj);

interface TableColumn {
  header: string;
  accessor: string;
  editable: boolean;
  format?: "number";
  /** Force the editor. Length stays text so sizes like "2100*200" can be typed. */
  editor?: "text" | "number" | "date";
}

interface TableRowData {
  [key: string]: any;
}

interface Action {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: (row: TableRowData) => void;
  visible?: (row: TableRowData) => boolean;
}

interface ServerPaginationConfig {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

interface EnabledToggleConfig {
  onToggle: (row: TableRowData, enabled: boolean) => void | Promise<void>;
  isToggling?: (rowId: string) => boolean;
}

interface DataTableProps {
  columns: TableColumn[];
  data: TableRowData[];
  setData?: (data: TableRowData[]) => void;
  setSearchTerm?: (searchTerm: string) => void;
  actions?: Action[];
  loading?: boolean;
  pagination?: ServerPaginationConfig;
  onCellEdit?: (row: TableRowData, column: string, newValue: string, oldValue: any) => void | Promise<void>;
  enabledToggle?: EnabledToggleConfig;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  setData,
  setSearchTerm,
  actions,
  loading = false,
  pagination,
  onCellEdit,
  enabledToggle,
}) => {
  const isMobile = useIsMobile();
  const isServerPaginated = !!pagination;
  const [clientCurrentPage, setClientCurrentPage] = useState(1);
  const [clientPageSize, setClientPageSize] = useState(10);

  const currentPage = isServerPaginated ? pagination.page : clientCurrentPage;
  const pageSize = isServerPaginated ? pagination.pageSize : clientPageSize;
  const totalItems = isServerPaginated ? pagination.totalItems : data.length;
  const totalPages = isServerPaginated
    ? Math.max(1, pagination.totalPages)
    : Math.max(1, Math.ceil(totalItems / pageSize));

  const startIndex = (currentPage - 1) * pageSize;
  const visibleData = isServerPaginated
    ? data
    : data.slice(startIndex, startIndex + pageSize);

  const showingStart = totalItems === 0 ? 0 : startIndex + 1;
  const showingEnd = totalItems === 0 ? 0 : startIndex + visibleData.length;

  const formatDisplayValue = (val: any, format?: "number"): string => {
    if (val === undefined || val === null) return "---";
    const s = String(val).trim();
    if (s === "" || s.toLowerCase() === "undefined") return "---";
    if (format === "number") {
      const n = typeof val === "number" ? val : Number(s.replace(/,/g, ""));
      if (Number.isFinite(n)) return n.toLocaleString();
    }
    return s;
  };

  const goToPreviousPage = () => {
    if (isServerPaginated) {
      pagination.onPageChange(Math.max(1, currentPage - 1));
      return;
    }
    setClientCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    if (isServerPaginated) {
      pagination.onPageChange(Math.min(totalPages, currentPage + 1));
      return;
    }
    setClientCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handlePageSizeChange = (size: number) => {
    if (isServerPaginated) {
      pagination.onPageSizeChange(size);
      pagination.onPageChange(1);
      return;
    }
    setClientPageSize(size);
    setClientCurrentPage(1);
  };

  /** Returns false when the edit was rejected (e.g. API failure) so callers can keep the editor open. */
  const commitCellEdit = async (
    row: TableRowData,
    index: number,
    column: TableColumn,
    newValue: string,
  ): Promise<boolean> => {
    const dataIndex = isServerPaginated ? index : startIndex + index;

    // Editing the first column to a value that already exists (client mode only)
    if (
      setData &&
      column.accessor === columns[0].accessor &&
      data.some(
        (r, i) =>
          i !== dataIndex &&
          String(getValue(r, column.accessor)).toLowerCase() ===
          String(newValue).toLowerCase(),
      )
    ) {
      const firstKey = columns[0].accessor;
      const filteredData = data.filter((r) => getValue(r, firstKey) !== undefined);
      if (typeof setSearchTerm === "function") {
        setSearchTerm(String(newValue));
        setData(filteredData);
      }
      return true;
    }

    if (onCellEdit) {
      const currentRow = data[dataIndex] ?? row;
      const oldValue = getValue(currentRow, column.accessor);
      try {
        await onCellEdit(currentRow, column.accessor, newValue, oldValue);
      } catch (error) {
        toast.error(getBackendErrorMessage(error, "Failed to save cell edit"));
        return false;
      }
    }

    if (setData) {
      const updatedData = data.map((r, i) =>
        i === dataIndex ? { ...r, [column.accessor]: newValue } : r,
      );
      setData(updatedData);
    }
    return true;
  };

  if (isMobile) {
    return (
      <MobileDataList
        columns={columns}
        rows={visibleData}
        actions={actions}
        loading={loading}
        enabledToggle={enabledToggle}
        getValue={getValue}
        formatValue={formatDisplayValue}
        onEditCell={commitCellEdit}
        pager={{
          currentPage,
          totalPages,
          totalItems,
          showingStart,
          showingEnd,
          onPrevious: goToPreviousPage,
          onNext: goToNextPage,
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 relative">
      <div className="rounded-md border border-gray-100 relative">
        {loading && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white/80"
            aria-hidden="true"
          >
            <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
          </div>
        )}
        <Table wrapperClassName="overflow-x-auto">
          <TableHeader className="sticky top-0 z-20 bg-gray-50 shadow-[0_1px_0_0_rgba(0,0,0,0.05)] [&>tr>th]:bg-gray-50">
            <TableHeaderRow>
              {columns.map((column) => (
                <TableColumnHeader key={column.accessor}>
                  {column.header}
                </TableColumnHeader>
              ))}
              {actions && actions.length > 0 && (
                <TableColumnHeader
                  width="80px"
                  isLast
                  className="sticky right-0 z-20 bg-gray-50 overflow-hidden"
                >
                  <div
                    className="absolute inset-0 bg-gradient-to-l from-gray-50 via-gray-50 to-transparent"
                    style={{ width: "150%", left: "-50%" }}
                  />
                </TableColumnHeader>
              )}
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {visibleData.map((row, index) => (
              <TableRow
                key={(row as any)?.id ?? index}
                className="group hover:bg-gray-50 transition-all duration-200 border text-nowrap"
              >
                {columns.map((column) => {
                  if (column.accessor === "icon_url") {
                    const raw = row[column.accessor];
                    const url = typeof raw === "string" ? raw.trim() : "";
                    return (
                      <TableCell key={column.accessor}>
                        {url ? (
                          <img
                            src={url}
                            alt=""
                            className="h-12 w-12 rounded-md object-cover border border-gray-100 bg-gray-50"
                          />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                    );
                  }

                  if (column.accessor === "status") {
                    return (
                      <TableCell key={column.accessor}>
                        <Badge variant={getBadgeVariant(row[column.accessor] as string)}>
                          {formatDisplayValue(row[column.accessor])}
                        </Badge>
                      </TableCell>
                    );
                  }

                  const cellValue = getValue(row, column.accessor);

                  if (column.accessor === "site_customer") {
                    return (
                      <TableCell key={column.accessor} className="whitespace-pre-line align-top">
                        {formatDisplayValue(cellValue)}
                      </TableCell>
                    );
                  }

                  if (column.accessor === "enabled" && enabledToggle) {
                    const rowId = String((row as TableRowData).id ?? "");
                    const isEnabled = Boolean(cellValue);
                    const toggling = enabledToggle.isToggling?.(rowId) ?? false;

                    return (
                      <TableCell key={column.accessor}>
                        <Switch
                          checked={isEnabled}
                          disabled={toggling}
                          onCheckedChange={(checked) => {
                            void enabledToggle.onToggle(row, checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={isEnabled ? "Disable" : "Enable"}
                        />
                      </TableCell>
                    );
                  }

                  if (!column.editable) {
                    return (
                      <TableCell key={column.accessor}>
                        {formatDisplayValue(cellValue, column.format)}
                      </TableCell>
                    );
                  }

                  const editableValue =
                    cellValue === undefined || cellValue === null ||
                      String(cellValue).trim().toLowerCase() === "undefined"
                      ? ""
                      : String(cellValue);

                  return (
                    <EditableTD
                      key={column.accessor}
                      value={editableValue}
                      displayValue={
                        column.format === "number"
                          ? formatDisplayValue(cellValue, "number")
                          : undefined
                      }
                      onChange={async (newValue) => {
                        await commitCellEdit(row, index, column, newValue);
                      }}
                      inputType={column.editor}
                      isFirstRow={index === 0 && currentPage === 1}
                    />
                  );
                })}
                {actions && actions.length > 0 && (
                  <td
                    className="sticky right-0 w-[180px] pr-6 overflow-hidden bg-transparent h-9"
                    style={{
                      borderRight: "none",
                      zIndex: 1,
                    }}
                  >
                    <div
                      className="absolute inset-0 bg-gradient-to-l from-white via-white to-transparent group-hover:from-gray-50 group-hover:via-gray-50"
                      style={{ width: "110%", left: "-10%" }}
                    />
                    <div className="relative flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          "h-8 px-3 text-[13px] whitespace-nowrap gap-1.5 transition-all duration-200",
                          "border border-gray-200 text-gray-700",
                          "hover:bg-gray-50 hover:text-gray-900",
                          "opacity-0 group-hover:opacity-100",
                          "group-hover:bg-gray-900 group-hover:text-white group-hover:border-gray-900",
                          "group-hover:hover:bg-gray-800",
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          actions[0].onClick(row);
                        }}
                      >
                        Details
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Button>
                      <div className="relative isolate flex rounded-lg border border-gray-200 divide-x divide-gray-200 bg-white">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-8 w-8 p-0 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-lg"
                            >
                              <Icons.MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-[200px]"
                          >
                            {actions
                              .filter((a) => !a.visible || a.visible(row))
                              .map((action, idx) => (
                                <DropdownMenuItem
                                  key={idx}
                                  className="text-[13px] whitespace-nowrap"
                                  onClick={() => action.onClick(row)}
                                >
                                  <action.icon className="mr-2 h-4 w-4" />
                                  {action.label}
                                </DropdownMenuItem>
                              ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </td>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-700">
            Showing {showingStart}-{showingEnd} of {totalItems} items
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                {pageSize} per page
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {[10, 50, 100].map((size) => (
                <DropdownMenuItem
                  key={size}
                  onClick={() => handlePageSizeChange(size)}
                >
                  {size} per page
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPreviousPage}
            disabled={currentPage === 1 || loading}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={goToNextPage}
            disabled={currentPage === totalPages || loading}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export type {
  TableRowData,
  TableColumn as DataTableColumn,
  Action as DataTableAction,
  EnabledToggleConfig,
};
export default DataTable;
